import React, { useState, useEffect, useRef, useId, useLayoutEffect } from 'react';
import { Info } from 'lucide-react';

// Module-level manager so only one tooltip is open at a time globally
let activeTooltipCloseFn: (() => void) | null = null;

export interface InfoTooltipProps {
  content: string;
  ariaLabel: string;
  id?: string;
  className?: string;
  iconClassName?: string;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  ariaLabel,
  id: customId,
  className = '',
  iconClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const autoId = useId();
  const tooltipId = customId || `info-tooltip-${autoId.replace(/:/g, '')}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Viewport containment offsets
  const [alignRight, setAlignRight] = useState(false);
  const [flipTop, setFlipTop] = useState(false);

  const openTooltip = () => {
    if (activeTooltipCloseFn && activeTooltipCloseFn !== closeSelf) {
      activeTooltipCloseFn();
    }
    activeTooltipCloseFn = closeSelf;
    setIsOpen(true);
  };

  const closeSelf = () => {
    setIsOpen(false);
    if (activeTooltipCloseFn === closeSelf) {
      activeTooltipCloseFn = null;
    }
  };

  const toggleTooltip = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOpen) {
      closeSelf();
    } else {
      openTooltip();
    }
  };

  // Check viewport boundaries on open to keep tooltip inside screen
  useLayoutEffect(() => {
    if (!isOpen || !tooltipRef.current || !triggerRef.current) return;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Check right edge overflow
    if (triggerRect.left + tooltipRect.width > viewportWidth - 12) {
      setAlignRight(true);
    } else {
      setAlignRight(false);
    }

    // Check bottom edge overflow
    if (triggerRect.bottom + tooltipRect.height > viewportHeight - 12) {
      setFlipTop(true);
    } else {
      setFlipTop(false);
    }
  }, [isOpen]);

  // Click outside and Escape key handlers
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeSelf();
        triggerRef.current?.focus();
      }
    };

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        tooltipRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      closeSelf();
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('touchstart', handleClickOutside);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-describedby={isOpen ? tooltipId : undefined}
        onClick={toggleTooltip}
        onMouseEnter={openTooltip}
        onMouseLeave={closeSelf}
        onFocus={openTooltip}
        onBlur={(e) => {
          if (
            tooltipRef.current &&
            tooltipRef.current.contains(e.relatedTarget as Node)
          ) {
            return;
          }
          closeSelf();
        }}
        className="p-1 rounded-full text-[#737373] hover:text-[#FAFAFA] focus:text-[#FAFAFA] focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-colors cursor-pointer select-none"
      >
        <Info className={`w-3.5 h-3.5 ${iconClassName}`} />
      </button>

      {isOpen && (
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className={`absolute z-50 w-64 max-w-[260px] p-3 rounded-xl bg-[#1C1C1C] border border-[#333333] shadow-2xl text-xs text-[#E5E5E5] leading-relaxed font-normal normal-case tracking-normal text-left pointer-events-auto transition-opacity duration-150 ${
            flipTop ? 'bottom-full mb-2' : 'top-full mt-2'
          } ${alignRight ? 'right-0' : 'left-0'}`}
        >
          {content}
        </div>
      )}
    </div>
  );
};
